import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import { isEqual, partition, sortBy, uniqBy, uniqWith } from 'lodash-es';
import moment from 'moment';
import type { Delta } from 'quill/core';
import Quill from 'quill/core';
import React, { useLayoutEffect, useState } from 'react';
import ActivityItem, { ActivitySeparator } from './ActivityItem';
import * as styles from './ControlBarActivity.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import InfiniteScrollWaypoint from '../../components/InfiniteScrollWaypoint';
import LoadingIndicator from '../../components/LoadingIndicator';
import SVGIcon from '../../components/SVGIcon';
import { EditorActions } from '../../editor/editorSlice';
import { useQuill } from '../../editor/QuillContext';
import isInlineTextInsertion from '../../editor/utils/isInlineTextInsertion';
import { gql } from '../../graphql/generated/gql';
import type { DocumentType } from '../../graphql/generated/gql';
import { AlphaPostActivityType } from '../../graphql/generated/graphql';
import archiveIcon from '../../icons/non-prefilled/16x16/archive.svg';
import commentIcon from '../../icons/non-prefilled/16x16/comment.svg';
import draftsIcon from '../../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../../icons/non-prefilled/16x16/edit.svg';
import publishIcon from '../../icons/non-prefilled/16x16/publish.svg';
import reorderIcon from '../../icons/non-prefilled/16x16/reorder.svg';
import replyIcon from '../../icons/non-prefilled/16x16/reply.svg';
import unarchiveIcon from '../../icons/non-prefilled/16x16/unarchive.svg';
import unpublishIcon from '../../icons/non-prefilled/16x16/unpublish.svg';
import verifiedIcon from '../../icons/non-prefilled/16x16/verifiedFilled.svg';
import { useGetUser } from '../../user/useAllUsers';
import exhaustiveMatchGuard from '../../utils/exhaustiveMatchGuard';
import { isFeatureEnabled } from '../../utils/features';
import Logger from '../../utils/Logger';
import getFirstName from '../../utils/text/getFirstName';
import commentThreadHash from '../../utils/url/commentThreadHash';
import historyPath from '../../utils/url/historyPath';
import CommentPreview from '../comment/CommentPreview';
import partitionAndGroupHistories from '../history/partitionAndGroupHistories';
import type { HistoryGroup } from '../history/partitionAndGroupHistories';
import sumHistoryStats from '../history/sumHistoryStats';
import Stats from '../Stats';
import useCanDisplayNewPostHistoryView from '../useCanDisplayNewPostHistoryView';
import collectThreadAndAnnotationIds from '../utils/collectThreadAndAnnotationIds';

export const CONTROL_BAR_ACTIVITY_MIN_HEIGHT = 265;

// Do initial fetch with a fixed time to enable caching
// TODO: This will not be needed when beforeAt becomes optional
export const POST_ACTIVITIES_END_OF_TIME = '2099-12-31T00:00:00.0Z';

const MAX_COMMENT_LINE_COUNT = 3;

gql(/** @complexity 2 */ `
  fragment ControlBarActivity_commentThread on CommentThread {
    id
    content
    resolvedAt
    comments {
      id
      insertedAt
    }
  }
`);

const CONTROL_BAR_ACTIVITY_QUERY = gql(/** @complexity 29 */ `
  query ControlBarActivity($postId: ID!, $beforeAt: Datetime!) {
    alphaPostActivities(postId: $postId, beforeAt: $beforeAt) {
      actedAt
      action
      actors {
        id
        ...useGetUser_user
      }
      source {
        id
        ... on Comment {
          content
          thread {
            id
            ...ControlBarActivity_commentThread
          }
        }
        ... on CommentThread {
          id
          ...ControlBarActivity_commentThread
        }
      }
    }
    post(id: $postId) {
      id
      threads {
        id
      }
      histories {
        id
        ...partitionAndGroupHistories_history
        ...sumHistoryStats_history
        authors {
          id
          ...useGetUser_user
        }
      }
      ...historyPath_post
    }
  }
`);

interface ControlBarActivityProps {
  postId: string;
  closePopover: () => void;
  fullHeight?: boolean;
  lastViewedAt: number;
}

const ControlBarActivity = ({
  postId,
  closePopover,
  lastViewedAt,
  fullHeight,
}: ControlBarActivityProps) => {
  const { data, loading, fetchMore } = useQuery(CONTROL_BAR_ACTIVITY_QUERY, {
    variables: {
      postId,
      beforeAt: POST_ACTIVITIES_END_OF_TIME,
    },
  });

  const historyActivities = useEditActivities(
    data?.alphaPostActivities,
    data?.post,
  );

  const commentActivities = useCommentActivities(
    data?.alphaPostActivities,
    data?.post,
    closePopover,
  );

  // TODO: Add error handling
  if (loading || !data) {
    return (
      <div className={styles.loader}>
        <LoadingIndicator delay={0} />
      </div>
    );
  }

  const fetchMoreActivities = async () => {
    const lastActivity = data.alphaPostActivities?.at(-1);
    if (!lastActivity) {
      return;
    }

    // There might be two activities with the same timestamp on the edge of two pages.
    // To ensure we don't skip any of them, we slightly over-fetch to include
    // the last activity from the previous page on the next page.
    const beforeAt = moment(lastActivity.actedAt).add(1, 'ms').toISOString();

    await fetchMore({
      variables: {
        postId,
        beforeAt,
      },
      updateQuery(previousResult, { fetchMoreResult }) {
        return {
          ...previousResult,
          ...fetchMoreResult,
          alphaPostActivities: uniqWith(
            [
              ...previousResult.alphaPostActivities,
              ...fetchMoreResult.alphaPostActivities,
            ],
            isEqual,
          ),
        };
      },
    });
  };

  const recentActivitiesThreshold = lastViewedAt ? moment(lastViewedAt) : null;
  const lastFetchedActivity = data.alphaPostActivities.at(-1);

  const standardActivities: ActivityView[] = data.alphaPostActivities
    .map((item) => {
      if (
        item.action === AlphaPostActivityType.Edit ||
        item.action === AlphaPostActivityType.CommentCreate ||
        item.action === AlphaPostActivityType.CommentReply ||
        item.action === AlphaPostActivityType.CommentResolve
      ) {
        return null;
      }

      return {
        key: `${item.actedAt}-${item.action}`,
        timestamp: new Date(item.actedAt).getTime(),
        view: <StandardPostActivity action={item.action} activity={item} />,
      };
    })
    .filter((item) => item != null);

  const allActivities = uniqBy(
    [...standardActivities, ...historyActivities, ...commentActivities],
    (item) => item.key,
  );
  const allActivitiesSorted = sortBy(allActivities, (item) => -item.timestamp);

  const [recentActivities, oldActivities] = partition(
    allActivitiesSorted,
    (item) => recentActivitiesThreshold?.isBefore(item.timestamp),
  );

  const hasMoreActivitiesToFetch =
    lastFetchedActivity &&
    lastFetchedActivity.action !== AlphaPostActivityType.Create;

  return (
    <div
      className={classNames(styles.container, {
        [styles.fullHeight]: fullHeight,
      })}
    >
      <h3 className={styles.title}>Activities</h3>
      <div>
        {recentActivities?.map((activity) => (
          <React.Fragment key={activity.key}>{activity.view}</React.Fragment>
        ))}
        {oldActivities.length > 0 && (
          <>
            {recentActivitiesThreshold && (
              <ActivitySeparator>
                Your last view {recentActivitiesThreshold.fromNow()}
              </ActivitySeparator>
            )}
            {oldActivities?.map((activity) => (
              <React.Fragment key={activity.key}>
                {activity.view}
              </React.Fragment>
            ))}
          </>
        )}
      </div>
      {hasMoreActivitiesToFetch && (
        <InfiniteScrollWaypoint fetchMore={fetchMoreActivities} />
      )}
    </div>
  );
};

const useEditActivities = (
  activities:
    | DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['alphaPostActivities']
    | undefined,
  post: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post'] | undefined,
): ActivityView[] => {
  const getUser = useGetUser();
  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();

  if (!activities || !post) {
    return [];
  }

  const { accessibleHistories, restrictedHistories } =
    partitionAndGroupHistories(post.histories);
  const lastFetchedActivity = activities.at(-1);

  return [...accessibleHistories, ...restrictedHistories]
    .filter(
      (item) =>
        new Date(lastFetchedActivity?.actedAt) <= new Date(item.insertedAt),
    )
    .map((item): ActivityView => {
      const stats = sumHistoryStats(item.histories);
      const hasStats = Object.values(stats).some((stat) => stat > 0);
      const actors = uniqBy(
        item.histories.flatMap((history) => history.authors.map(getUser)),
        (user) => user.id,
      );
      return {
        key: `${item.insertedAt}-history}`,
        timestamp: new Date(item.insertedAt).getTime(),
        view: (
          <ActivityItem
            actors={actors}
            icon={<SVGIcon className={styles.editIcon}>{editIcon}</SVGIcon>}
            timestamp={item.insertedAt}
            description={
              hasStats ? (
                <Stats stats={sumHistoryStats(item.histories)} />
              ) : (
                'Edited this post'
              )
            }
            href={getLinkToHistory(post, canDisplayNewPostHistoryView, item)}
          />
        ),
      };
    });
};

const useCommentActivities = (
  activities:
    | DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['alphaPostActivities']
    | undefined,
  post: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post'] | undefined,
  onSelectThread: () => void,
): ActivityView[] => {
  const { quill } = useQuill();
  const [postThreadsIds, setPostThreadsIds] = useState(new Set<string>());
  const getUser = useGetUser();
  const dispatch = useAppDispatch();

  const canDisplayNewPostHistoryView = useCanDisplayNewPostHistoryView();

  useLayoutEffect(() => {
    if (!quill) return undefined;

    const updatePostThreadsIds = () => {
      const result = collectThreadAndAnnotationIds(quill.getContents().ops);
      setPostThreadsIds((current) =>
        isEqual(current, result.allThreadIds) ? current : result.allThreadIds,
      );
    };

    updatePostThreadsIds();

    const onTextChange = (delta: Delta) => {
      if (isInlineTextInsertion(delta)) return;
      updatePostThreadsIds();
    };

    quill.on(Quill.events.TEXT_CHANGE, onTextChange);

    return () => {
      quill.off(Quill.events.TEXT_CHANGE, onTextChange);
    };
  }, [quill]);

  if (!activities || !post) {
    return [];
  }

  const visibleCommentThreadsIds = post.threads
    .map((thread) => (postThreadsIds.has(thread.id) ? thread.id : null))
    .filter((item) => item != null);

  const focusCommentThread = (threadId: string) => {
    dispatch(
      EditorActions.focusMagneticWall({
        id: threadId,
        type: 'comment',
        isActive: true,
        hasExpandIntent: false,
      }),
    );
    onSelectThread();
  };

  const icons = {
    [AlphaPostActivityType.CommentCreate]: (
      <SVGIcon className={styles.commentIcon}>{commentIcon}</SVGIcon>
    ),
    [AlphaPostActivityType.CommentReply]: (
      <SVGIcon className={styles.commentIcon}>{replyIcon}</SVGIcon>
    ),
    [AlphaPostActivityType.CommentResolve]: (
      <SVGIcon className={styles.resolveIcon}>{commentIcon}</SVGIcon>
    ),
  } as const;

  const { accessibleHistories, restrictedHistories } =
    partitionAndGroupHistories(post.histories);

  const allHistories = [...accessibleHistories, ...restrictedHistories];

  return activities
    .map((activity) => {
      const actors = activity.actors.map(getUser);

      const content =
        activity.source?.__typename === 'CommentThread' ||
        activity.source?.__typename === 'Comment'
          ? activity.source.content
          : null;

      const thread =
        activity.source?.__typename === 'CommentThread'
          ? activity.source
          : activity.source?.__typename === 'Comment'
            ? activity.source.thread
            : null;

      const icon = icons[activity.action as keyof typeof icons];

      if (!content || !thread || !icon) {
        return null;
      }

      const getLinkToThreadInHistory = () => {
        const historyItem = allHistories.findLast((group) => {
          const threadCreatedAt = thread.comments[0]?.insertedAt;
          const groupStart = group.histories.at(0)?.insertedAt;
          const groupEnd = group.histories.at(-1)?.insertedAt;
          return (
            moment(groupStart).isBetween(threadCreatedAt, thread.resolvedAt) ||
            moment(groupEnd).isBetween(threadCreatedAt, thread.resolvedAt)
          );
        });

        return getLinkToHistory(
          post,
          canDisplayNewPostHistoryView,
          historyItem,
          thread.id,
        );
      };

      return {
        key: `${activity.actedAt}-${activity.action}`,
        timestamp: new Date(activity.actedAt).getTime(),
        view: (
          <ActivityItem
            actors={actors}
            timestamp={activity.actedAt}
            icon={icon}
            description={
              <CommentPreview
                collapseLines
                content={content}
                link={false}
                maxLines={MAX_COMMENT_LINE_COUNT}
              />
            }
            {...(visibleCommentThreadsIds.includes(thread.id)
              ? {
                  onClick: () => focusCommentThread(thread.id),
                  onMouseEnter: () =>
                    dispatch(EditorActions.highlightComment(thread.id)),
                  onMouseLeave: () =>
                    dispatch(EditorActions.removeMagneticWallHighlight()),
                }
              : {
                  href: getLinkToThreadInHistory(),
                })}
          />
        ),
      };
    })
    .filter((item) => item != null);
};

const getLinkToHistory = (
  post: DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post'],
  canDisplayNewPostHistoryView: boolean | null,
  historyGroup?: HistoryGroup<
    DocumentType<typeof CONTROL_BAR_ACTIVITY_QUERY>['post']['histories'][number]
  >,
  threadId?: string,
) => {
  if (!historyGroup) {
    return undefined;
  }

  const path =
    historyPath(post, historyGroup.versionFrom, historyGroup.versionTo) +
    (threadId ? commentThreadHash(threadId) : '');

  if (!isFeatureEnabled('history_v2')) {
    return threadId ? undefined : path;
  }

  return historyGroup.isAccessible && canDisplayNewPostHistoryView
    ? path
    : undefined;
};

interface StandardPostActivityProps {
  action: Exclude<
    AlphaPostActivityType,
    | AlphaPostActivityType.Edit
    | AlphaPostActivityType.CommentCreate
    | AlphaPostActivityType.CommentReply
    | AlphaPostActivityType.CommentResolve
  >;
  activity: Omit<
    DocumentType<
      typeof CONTROL_BAR_ACTIVITY_QUERY
    >['alphaPostActivities'][number],
    'action'
  >;
}

const StandardPostActivity = ({
  action,
  activity,
}: StandardPostActivityProps) => {
  const getUser = useGetUser();
  const actors = activity.actors.map(getUser);

  const descriptionWithActors = (description: React.ReactNode) => (
    <div className={styles.activityDescription}>
      <strong>
        {activity.actors
          .map((actor) => getFirstName(getUser(actor).name))
          .join(', ')}
      </strong>{' '}
      {description}
    </div>
  );

  switch (action) {
    case AlphaPostActivityType.Create:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.createIcon}>{draftsIcon}</SVGIcon>}
          description={descriptionWithActors('created this post')}
        />
      );
    case AlphaPostActivityType.Verify:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.verifyIcon}>{verifiedIcon}</SVGIcon>}
          description={descriptionWithActors('verified this post')}
        />
      );
    case AlphaPostActivityType.Publish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.publishIcon}>{publishIcon}</SVGIcon>}
          description={descriptionWithActors('published this post')}
        />
      );
    case AlphaPostActivityType.Unpublish:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unpublishIcon}>{unpublishIcon}</SVGIcon>
          }
          description={descriptionWithActors('unpublished this post')}
        />
      );
    case AlphaPostActivityType.Archive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={<SVGIcon className={styles.archiveIcon}>{archiveIcon}</SVGIcon>}
          description={descriptionWithActors('archived this post')}
        />
      );
    case AlphaPostActivityType.Unarchive:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.unarchiveIcon}>{unarchiveIcon}</SVGIcon>
          }
          description={descriptionWithActors('unarchived this post')}
        />
      );
    case AlphaPostActivityType.TransferOwner:
      return (
        <ActivityItem
          actors={actors}
          timestamp={activity.actedAt}
          icon={
            <SVGIcon className={styles.transferIcon}>{reorderIcon}</SVGIcon>
          }
          description={descriptionWithActors(
            <>
              transferred ownership
              {activity.source?.__typename === 'User' && (
                <>
                  {' '}
                  to{' '}
                  <strong>{getFirstName(getUser(activity.source).name)}</strong>
                </>
              )}
            </>,
          )}
        />
      );

    default: {
      exhaustiveMatchGuard(action);
      const error = `Unknown activity type: ${action as string}`;
      if (process.env.NODE_ENV !== 'production') {
        throw new Error(error);
      }
      Logger.error(error);
      return null;
    }
  }
};

interface ActivityView {
  timestamp: number;
  key: string;
  view: React.ReactNode;
}

export default ControlBarActivity;
